import React from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { assignRemedies } from '../../utils/remediesBankEngine';
import { Sparkles, Shield, AlertTriangle, HeartHandshake } from 'lucide-react';

interface EnhancedRemedyRecommendationsProps {
  kundali: CompleteKundali;
  themeId: LifeThemeId;
  lang: Language;
  userEntitlement?: 'BASIC' | 'PREMIUM' | 'ULTRA_PREMIUM';
}

export const EnhancedRemedyRecommendations: React.FC<EnhancedRemedyRecommendationsProps> = ({
  kundali,
  themeId,
  lang,
  userEntitlement = 'BASIC'
}) => {
  const isHi = lang === 'hi';
  const assigned = assignRemedies(kundali, themeId, userEntitlement, lang);

  const primaryRemedies = assigned.filter((a) => a.rank === 'PRIMARY');
  const secondaryRemedies = assigned.filter((a) => a.rank === 'SECONDARY');

  return (
    <div
      id="enhanced-remedies-recommendation-card"
      className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-7 shadow-xs space-y-6"
    >
      {/* 1. Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-astro-border/80 pb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-astro-primary/10 text-astro-primary px-2.5 py-0.5 rounded-full border border-astro-primary/20 flex items-center gap-1">
              <Shield className="w-3 h-3 text-astro-accent" />
              {isHi ? 'वैदिक उपचारात्मक मार्गदर्शन' : 'Personalized Vedic Guidance'}
            </span>
            <span className="text-xs text-astro-secondary-text">
              {isHi ? 'शास्त्रीय मंत्र, सेवा व सकारात्मक ऊर्जा' : 'Traditional Mantras, Charity & Balancing'}
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-text mt-1.5">
            {isHi ? 'आपके जीवन क्षेत्र हेतु उपचारात्मक मार्गदर्शन' : 'Harmonizing Remedies for Your Active Life Areas'}
          </h3>
        </div>
      </div>

      {/* 2. Primary Recommended Practices */}
      <div className="space-y-4">
        <div className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-astro-accent" />
          <span>{isHi ? 'मुख्य अनुशंसित साधना व क्रियाएं:' : 'Primary Recommended Practices:'}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {primaryRemedies.map((cand, idx) => (
            <div
              key={idx}
              className="bg-[#FAF6ED] rounded-xl p-4 sm:p-5 border border-astro-border shadow-2xs space-y-3 flex flex-col justify-between"
            >
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                    {cand.remedy.category}
                  </span>
                  <span className="text-xs font-bold text-astro-accent">
                    {cand.remedy.planet}
                  </span>
                </div>

                <h4 className="text-sm font-bold text-astro-text">
                  {isHi ? cand.remedy.nameHi : cand.remedy.name}
                </h4>

                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi ? cand.whyThisRemedyHi : cand.whyThisRemedy}
                </p>

                {/* Routine / Timing */}
                <div className="bg-astro-bg p-3 rounded-lg border border-astro-border text-xs space-y-1 text-astro-secondary-text">
                  <div>
                    <strong className="text-astro-primary font-semibold">{isHi ? 'नियम व समय:' : 'Routine & Time:'} </strong>
                    <span>{cand.remedy.recommendedTime} ({cand.remedy.frequency})</span>
                  </div>
                  <div>
                    <strong className="text-astro-primary font-semibold">{isHi ? 'शास्त्रीय आधार:' : 'Classical Tradition:'} </strong>
                    <span>{cand.remedy.traditionalBasis}</span>
                  </div>
                </div>

                {/* Contraindication check */}
                {cand.contraindicationCheck.warningNote && (
                  <div className="bg-amber-50 border border-amber-200 p-2.5 rounded-lg text-xs text-amber-900 flex items-start gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <span>{isHi ? cand.contraindicationCheck.warningNoteHi : cand.contraindicationCheck.warningNote}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. Supportive Seva & Acts of Kindness */}
      {secondaryRemedies.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text flex items-center gap-1.5">
            <HeartHandshake className="w-4 h-4 text-astro-accent" />
            <span>{isHi ? 'सहायक सेवा, दान एवं सद्कर्म:' : 'Supportive Seva & Acts of Kindness:'}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {secondaryRemedies.map((cand, idx) => (
              <div key={idx} className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-border text-xs space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-primary">{isHi ? cand.remedy.nameHi : cand.remedy.name}</span>
                  <span className="text-[10px] font-semibold text-astro-accent">{cand.remedy.recommendedDay}</span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">{isHi ? cand.whyThisRemedyHi : cand.whyThisRemedy}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
